import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChevronDown } from "lucide-react";
import { BookingForm } from "@/components/BookingForm";
import { HERO_LINES, HERO_IMAGE, PRIMARY_TEL, COMPANY } from "@/data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.9]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_IMAGE}
          data-testid="hero-video"
        >
          <source src="/hero.mp4" type="video/mp4" />
        </video>
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-black/60" />

      <div className="relative z-10 mx-auto grid min-h-screen max-w-[1400px] grid-cols-1 items-center gap-12 px-6 pb-24 pt-36 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-6 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-primary" />
            <span className="text-xs uppercase tracking-[0.3em] text-primary">{COMPANY.city}</span>
          </motion.div>

          <h1 className="font-serif text-5xl font-light leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-8xl">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden py-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.5 + i * 0.13 }}
                  className={`block ${i === 2 ? "italic text-primary" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-zinc-300 md:text-lg"
          >
            Luxury, reliable transportation across Washington, DC and the greater DMV.
            Over 10,000 customers and companies choose Admiral for every ride.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-book-ride-button"
              className="bg-primary px-8 py-4 text-xs font-medium uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300 hover:tracking-[0.3em]"
            >
              Book a Ride
            </button>
            <a
              href={`tel:${PRIMARY_TEL}`}
              data-testid="hero-call-button"
              className="group flex items-center gap-3 border border-white/20 px-8 py-4 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              <Phone className="h-4 w-4" /> Call Now
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          id="book"
          className="scroll-mt-28 lg:col-span-5"
        >
          <div className="border border-white/10 bg-black/60 p-8 shadow-[0_8px_40px_rgba(0,0,0,0.6)] backdrop-blur-2xl">
            <div className="mb-6">
              <span className="text-xs uppercase tracking-[0.3em] text-primary">Reserve Now</span>
              <h3 className="mt-2 font-serif text-3xl font-light text-white">Book your chauffeur</h3>
            </div>
            <BookingForm />
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 lg:left-10 lg:translate-x-0"
      >
        <ChevronDown className="h-6 w-6 animate-bounce text-zinc-500" />
      </motion.div>
    </section>
  );
};
